import React, { useState, useEffect, useRef } from "react";
import Header from "./Header";
import Footer from "./Footer";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import $ from "jquery";
import "datatables.net-bs5";
import { Link } from "react-router-dom";
import { API_URL, BASE_URL } from "./Config";
import config from "./common/Config";
import { setTableWidths, getTableWidths } from "../data/postAPi";

const ManifestList = () => {
  const [colWidths, setColWidths] = useState([40, 120, 120, 150, 150, 150, 150, 100, 80]);
  const salesOrderRef = useRef(null);
  const accessToken = localStorage.getItem("accessToken");
  const userId = localStorage.getItem("userId");
  const tableName = "manifestListTable";

  // Debounce for saving column widths
  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
  const debouncedSetTableWidths = useRef(
    debounce((userId, tableName, widths) => {
      setTableWidths(userId, tableName, widths);
    }, 500)
  ).current;

  // Column resizing logic
  const startResizing = (index, e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = colWidths[index];
    const onMouseMove = (moveEvent) => {
      const newWidth = Math.max(50, startWidth + moveEvent.clientX - startX);
      setColWidths((widths) => {
        const updated = [...widths];
        updated[index] = newWidth;
        debouncedSetTableWidths(userId, tableName, updated);
        return updated;
      });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Fetch column widths on mount
  useEffect(() => {
    async function fetchWidths() {
      try {
        const res = await getTableWidths(userId, tableName);
        if (res?.data?.widths && Array.isArray(res.data.widths) && res.data.widths.length > 0) {
          setColWidths(res.data.widths);
        } else {
          setColWidths([40, 120, 120, 150, 150, 150, 150, 100, 80]);
        }
      } catch (e) {
        setColWidths([40, 120, 120, 150, 150, 150, 150, 100, 80]);
      }
    }
    fetchWidths();
  }, [userId, tableName]);

  // DataTable initialization (only on mount)
  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const $table = $(salesOrderRef.current);
    if ($.fn.DataTable.isDataTable($table)) {
      $table.DataTable().destroy();
    }
    $table.DataTable({
      processing: true,
      serverSide: true,
      pageLength: 50,
      ajax: {
        url: `${config.API_URL}/manifest/list`,
        type: "POST",
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        data: function (d) {
          return {
            ...d,
            draw: d.draw,
            start: d.start,
            length: d.length
          };
        },
        dataSrc: function (json) {
          return Array.isArray(json.data) ? json.data : [];
        },
        error: function (xhr, status, error) {
          console.error("DataTable AJAX error:", xhr.status, xhr.statusText, error, xhr.responseText);
        },
      },
      language: {
        paginate: { first: "First", last: "Last", next: "Next", previous: "Previous" },
        search: "Search:",
      },
      columns: [
        { data: "id", width: colWidths[0] + 'px' },
        { data: "date", width: colWidths[1] + 'px' },
        {
          data: "manifest_id",
          width: colWidths[2] + 'px',
          className: "text-center",
          render: function (data, type, row) {
            return row.manifest_id
              ? `<a href=\"${BASE_URL}/manifest_list/ManifestPrint/${row.manifest_id}\">${row.manifest_id}</a>`
              : "-";
          },
        },
        {
          data: "trip_id",
          width: colWidths[3] + 'px',
          render: function (data, type, row) {
            return data ? data : " ";
          }
        },
        { data: "driver", width: colWidths[4] + 'px' },
        { data: "route_number", width: colWidths[5] + 'px' },
        { data: "sales_person", width: colWidths[6] + 'px' },
        {
          data: "status",
          width: colWidths[7] + 'px',
          render: function (data) {
            return `<span class=\"btn btn-primary badge\">${data || ""}</span>`;
          },
        },
        {
          data: null,
          width: colWidths[8] + 'px',
          render: function (data, type, row) {
            return !row.trip_id
              ? `<a class=\"viewbtn\" href=\"${BASE_URL}/manifest_list/mark_delivery/${data.manifest_id}\" id=\"mark_deliver\">\n                <i class=\"fa fa-edit\"></i>\n              </a>`
              : "";
          },
        },
      ],
      columnDefs: [
        { orderable: false, targets: "_all" },
      ],
      drawCallback: function () {
        // Reattach resize handlers
        const resizers = document.querySelectorAll('#manifestlisttable .th-resizer');
        resizers.forEach((resizer, index) => {
          const handleMouseDown = (e) => {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            startResizing(index, e);
          };
          resizer.addEventListener('mousedown', handleMouseDown);
          resizer._resizerHandler = handleMouseDown;
        });
        // Reapply column widths after each draw
        $('#manifestlisttable thead th').each(function (idx) {
          $(this).css('width', colWidths[idx] + 'px');
        });
      }
    });
    // Cleanup on unmount
    return () => {
      if ($.fn.DataTable.isDataTable($table)) {
        $table.DataTable().destroy();
      }
    };
  }, [accessToken]);

  // Debounced update of column widths in DOM when colWidths changes
  useEffect(() => {
    const handler = setTimeout(() => {
      $('#manifestlisttable thead th').each(function (idx) {
        $(this).css('width', colWidths[idx] + 'px');
      });
    }, 150); // 150ms debounce
    return () => clearTimeout(handler);
  }, [colWidths]);

  return (
    <>
      <Header />
      <div className="breadcrumbs">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 col-lg-12 col-sm-12">
              <Link to="/Dashboard"><i className="fa fa-home"></i></Link> &gt;
              <Link to="/Dashboard">Dashboard</Link> &gt;
              <Link to="/ManifestList">Dispatch & Deliveries</Link> &gt;
              <span className="last-crumb">Manifest List</span>
            </div>
          </div>
        </div>
      </div>
      <div className="main-container">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12 col-lg-12 col-sm-12">
              <div className="dashboard manifest-list-table">
                <div className="verical-scrl-wrapper menifest-area" id="style-3" style={{ width: '100%' }}>
                  <table className="table table-bordered dataTable resizable" id="manifestlisttable" ref={salesOrderRef}>
                    <thead>
                      <tr>
                        {["#", "date", "Manifest Number", "Trip ID", "Driver", "Route", "Sales Person", "Status", "Action"].map((header, idx) => (
                          <th
                            key={header}
                            style={{ width: colWidths[idx], minWidth: 50, position: "relative" }}
                          >
                            {header}
                            {idx < colWidths.length - 1 && (
                              <div
                                className="th-resizer"
                                onMouseDown={(e) => startResizing(idx, e)}
                              />
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* DataTable will populate this */}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ManifestList;
